import type { CSSProperties } from 'react';
import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { RateMessages } from 'Components/widgets/rate-messages/RateMessages';
type Props = {
  className?: string;
  endDate: string;
  hasTransport?: boolean;
  id?: string;
  isJoyview?: boolean;
  onClose: () => void;
  onReset: () => void;
  price: string | null | undefined;
  priceIsLoading: boolean;
  rate?: string;
  realPrice: string | null | undefined;
  realPriceIsLoading: boolean;
  startDate: string;
  style?: CSSProperties;
};
export const BookingCalendarResume = ({
  className,
  endDate,
  hasTransport,
  id,
  isJoyview = false,
  onClose,
  onReset,
  price,
  priceIsLoading,
  rate,
  realPrice,
  realPriceIsLoading,
  startDate,
  ...rest
}: Props) => {
  const track = useTracking();

  const onClickSeeMore = (_, isExpanded) => {
    if (!isExpanded) {
      track({
        actionGA: 'calendarPrice',
        labelGA: 'viewMore',
      });
    }
  };

  return (
    <div {...rest} className={classnames('rounded-12 bg-white shadow', className)} id={id}>
      <div className="relative isolate">
        <button
          className="absolute end-0 top-0 m-10 text-14 leading-0"
          onClick={onClose}
          type="button"
        >
          <span className="sr-only">{t('common.close')}</span>
          <Icon name={ICONS.CROSS} width="1em" />
        </button>
        <div className="p-20">
          <p className="mb-30 flex items-start gap-10 text-12 sm:text-14">
            <span className="text-28 leading-0 text-sienna">
              <Icon name={ICONS.CHECK_OUTLINE} width="1em" />
            </span>{' '}
            <span>
              <TemplateString
                className="inline-flex gap-5"
                template={t('resort.startingPrice.stayDeparture')}
                values={{
                  departure_date: startDate.charAt(0).toUpperCase() + startDate.slice(1),
                }}
                valuesClassName={{
                  departure_date: 'text-sienna',
                }}
              />
              <br />
              <TemplateString
                className="inline-flex gap-5"
                template={t('resort.startingPrice.endDateLabel')}
                values={{
                  end_date: endDate.charAt(0).toUpperCase() + endDate.slice(1),
                }}
                valuesClassName={{
                  departure_date: 'text-sienna',
                }}
              />
            </span>
          </p>

          {(priceIsLoading || realPriceIsLoading) && (
            <div className="flex justify-center py-10">
              <Spinner size="small" />
            </div>
          )}
          {((realPrice && !realPriceIsLoading) || (price && !priceIsLoading)) && (
            <div className="mb-20 flex items-center justify-center gap-10 text-12">
              <span className="hidden text-18 leading-0 text-sienna md:inline-block">
                <Icon name={ICONS.ACCOMODATIONS} width="1em" />
                {hasTransport && <Icon className="ms-5" name={ICONS.TRANSPORT} width="1em" />}
              </span>
              <span>{`${t('price.format.unitTotalPerTripIndicative')} `}</span>
              <span className="text-20 font-bold text-sienna">{realPrice || price}</span>
            </div>
          )}

          <p className="flex flex-col text-center text-10 sm:text-12">
            {isJoyview ? (
              <span className="leading-1 text-blueWave">
                <Icon name={ICONS.CLUBMED_JOYVIEW} isNotSquare width="10em" />
              </span>
            ) : (
              <Fragment>
                {price && !priceIsLoading ? t('booking.form.priceIsLabel') : ''}{' '}
                <span className="font-brand text-14 leading-1 text-sienna sm:text-16">
                  <Icon className="-mb-2" name={ICONS.ALL_INCLUSIVE} />{' '}
                  {t('resort.startingPrice.allInclusive')}
                </span>
              </Fragment>
            )}
          </p>
        </div>
        <button
          className="w-full px-20 py-10 text-center text-14 underline"
          onClick={() => {
            onReset();
            track({
              actionGA: 'calendarPrice',
              labelGA: 'resetDates',
            });
          }}
          type="button"
        >
          {t('booking.priceCalendar.resetCalendar')}
        </button>
        {!!rate && (
          <div className="font-text-start border-t-1 border-mediumGrey p-20">
            <SeeMore
              className="mt-10 flex-1 md:mt-0"
              classNameButton="text-end"
              hasNoScrollOnCollapse
              isCollapseEnabled
              onClick={onClickSeeMore}
              seeMoreSize={70}
              seeMoreSizeTolerance={0}
            >
              <RateMessages rate={rate} />
            </SeeMore>
          </div>
        )}
      </div>
    </div>
  );
};
